import React, { useEffect, useRef, useState } from 'react';

/**
 * 아이소메트릭 3D 장면 하나.
 *
 *   <Iso3D kind="cell" live parallax>{fallback}</Iso3D>
 *   <Iso3D kind="step" arg={stepIndex} live parallax>{fallback}</Iso3D>
 *
 * three.js 엔진과 씬 빌더는 첫 렌더 뒤에 지연 로딩한다. 그동안 children(SVG 폴백)이
 * 보이므로 LCP 는 텍스트·SVG 로 잡히고, 3D 는 준비되면 페이드인한다.
 * WebGL 이 없거나 로딩에 실패하면 폴백이 그대로 남는다. disabled 면 3D 를 시도하지 않는다(폰).
 *
 * kind 가 바뀌면 뷰를 다시 만들고, arg 만 바뀌면 씬만 교체한다(캔버스 유지).
 */
let enginePromise = null;
function loadEngine() {
  if (!enginePromise) {
    enginePromise = Promise.all([import('./isoEngine'), import('./isoBuilders')]).then(([engine, builders]) => ({ engine, builders }));
  }
  return enginePromise;
}

export default function Iso3D({ kind, arg, live = false, parallax = false, maxDpr, disabled = false, className = '', style, children }) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const modRef = useRef(null);
  const cacheRef = useRef(new Map());
  const [ready, setReady] = useState(false);
  const [fallbackGone, setFallbackGone] = useState(false);
  const [failed, setFailed] = useState(false);

  const getBuilt = (k, a) => {
    const key = `${k}:${a ?? ''}`;
    let built = cacheRef.current.get(key);
    if (!built) {
      built = modRef.current.builders.buildScene(k, a);
      cacheRef.current.set(key, built);
    }
    return built;
  };

  useEffect(() => {
    if (disabled) return undefined;
    let cancelled = false;
    let view = null;
    const host = hostRef.current;
    if (!host) return undefined;

    // Phones / weak CPUs: wait for window load + a beat so hydration and LCP finish first.
    const low = window.matchMedia('(max-width: 768px)').matches || (navigator.hardwareConcurrency || 8) <= 4;
    const idle = window.requestIdleCallback ? cb => window.requestIdleCallback(cb, { timeout: low ? 4000 : 1200 }) : cb => setTimeout(cb, 120);
    const afterLoad = cb => {
      if (!low) return cb();
      const go = () => setTimeout(cb, 900);
      return document.readyState === 'complete' ? go() : window.addEventListener('load', go, { once: true });
    };
    afterLoad(() => idle(() => {
      loadEngine()
        .then(mod => {
          if (cancelled || !hostRef.current) return;
          if (!mod.engine.webglAvailable()) {
            setFailed(true);
            return;
          }
          modRef.current = mod;
          view = mod.engine.mountView(hostRef.current, getBuilt(kind, arg), { live, parallax, maxDpr });
          viewRef.current = view;
          setReady(true);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }));

    return () => {
      cancelled = true;
      if (view && modRef.current) modRef.current.engine.unmountView(view);
      viewRef.current = null;
      setReady(false);
      setFallbackGone(false);
    };
    // arg 변경은 아래 훅에서 씬 교체로 처리한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, live, parallax, disabled]);

  useEffect(() => {
    if (!viewRef.current || !modRef.current) return;
    modRef.current.engine.swapScene(viewRef.current, getBuilt(kind, arg));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg]);

  // 캔버스가 페이드인하는 동안 폴백을 밑에 두었다가 치운다.
  useEffect(() => {
    if (!ready) return undefined;
    const id = setTimeout(() => setFallbackGone(true), 700);
    return () => clearTimeout(id);
  }, [ready]);

  return (
    <div className={`iso3d ${ready ? 'iso3d--on' : 'iso3d--off'} ${failed ? 'iso3d--failed' : ''} ${className}`} style={style} aria-hidden="true">
      {!disabled && <div ref={hostRef} className="iso3d-host" />}
      {!fallbackGone && <div className={`iso3d-fallback ${ready ? 'is-leaving' : ''}`}>{children}</div>}
    </div>
  );
}
